import React, { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './assets/styles/broox.scss';
import './assets/styles/tagify.css';
import { ValueCard } from './components/cards/ValueCard';
import { GenderCard } from './components/cards/GenderCard';
import { AgeCard } from './components/cards/AgeCard';
import { EmotionCard } from './components/cards/EmotionCard';
import { ViewsIcon } from './components/icons/ViewsIcon';
import { TrafficIcon } from './components/icons/TrafficIcon';
import { DwellIcon } from './components/icons/DwellIcon';
import { AttentionIcon } from './components/icons/AttentionIcon';
import { InteractionsMetricsCard } from './components/cards/InteractionsMetricsCard';
import { TopFiveCard } from './components/cards/TopFiveCard';
import { TopInteractedCard } from './components/cards/TopInteractedCard';
import { HistoricalCard } from './components/cards/HistoricalCard';
import { Audience } from './model/Audience';
import { Occupancy, hasValues } from './model/Occupancy';
import { Header } from './components/header/Header';
import { Interaction } from './model/Interaction';
import { Filter, FilterService } from './model/Filter';
import { AccountContext } from './contexts/AccountContext';
import { EntriesCard } from './components/cards/EntriesCard';
import { OccupancyCard } from './components/cards/OccupancyCard';
import { PeakTimeCard } from './components/cards/PeakTimeCard';
import { OffPeakTimeCard } from './components/cards/OffPeakTimeCard';
import { MediaPlaybackCard } from './components/cards/MediaPlaybackCard';
import { MediaPlaybackAggregate as MediaPlayback } from './model/MediaPlayback';
import { AccountParams } from './model/AccountParams';
import { getExcelWorkbook } from './model/Download';
import { LocalDate, LocalTime, ComparePreset, RangePreset } from './model/datePicker/types'
import { createInitialPickerState, resolveB } from './model/datePicker/pickerState';
import { resolveCutoff } from './model/datePicker/resolveCutoff';
import { filterPatchFromApplied } from './components/reactDatePickerV3/filterAdapter';
import { IServiceFactory } from './services/IServiceFactory';
import { ProductSalesCard } from './components/cards/ProductSalesCard';
import { Sales } from './model/ProductSales';
import { loadDashboard } from './model/Dashboard';
import { t, language, getLanguageParam } from './i18n';

const App = ({ params, autoRefresh, serviceFactory }: Props) => {
  const getFilterFromParams = (): Filter => {
    const searchParams = new URLSearchParams(window.location.search);
    const locationId = searchParams.getAll('location_id');
    const installationId = searchParams.get('installation_id');
    const campaignId = searchParams.get('campaign_id');
    const playlistId = searchParams.get('playlist_id');
    const zone = searchParams.get('zone');
    const gate = searchParams.get('gate');
    const crossingLine = searchParams.get('crossing_line');
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    const compareStartDate = searchParams.get('compare_start_date');
    const compareEndDate = searchParams.get('compare_end_date');
    const cutoffParam = searchParams.get('cutoff_time');
    const comparePresetParam = searchParams.get('compare_preset');
    const rangePresetParam = searchParams.get('range_preset');
    const now = DateTime.now();
    // Zone, gate and crossing line share zone_hits in the API: keep only one,
    // with the same priority the API applies (crossingLine > gate > zone)
    const filter = {
      locationId: locationId.length ? locationId : undefined,
      installationId: installationId || undefined,
      campaignId: campaignId || undefined,
      playlistId: playlistId || undefined,
      zone: !crossingLine && !gate ? zone || undefined : undefined,
      gate: !crossingLine ? gate || undefined : undefined,
      crossingLine: crossingLine || undefined
    };
    if(!startDate) {
      const today = now.toFormat('yyyy-MM-dd') as LocalDate;
      const initial = createInitialPickerState(today);
      return {
        ...filter,
        ...filterPatchFromApplied({
          a: initial.a,
          b: resolveB(initial),
          cutoff: resolveCutoff(initial.a, today, { hour: now.hour, minute: now.minute }),
          rangePreset: initial.rangePreset,
          comparePreset: initial.comparePreset
        })
      };
    }
    return {
      ...filter,
      from: DateTime.fromISO(startDate).toJSDate(),
      to: endDate ? DateTime.fromISO(endDate).toJSDate() : now.endOf('day').toJSDate(),
      compareFrom: compareStartDate ? DateTime.fromISO(compareStartDate).toJSDate() : undefined,
      compareTo: compareEndDate ? DateTime.fromISO(compareEndDate).toJSDate() : undefined,
      cutoff: (cutoffParam as LocalTime) || null,
      comparePreset: (comparePresetParam as ComparePreset) || undefined,
      rangePreset: (rangePresetParam as RangePreset) || undefined
    };
  }

  const [accountContext, setAccountContext] = useState(params);
  const [categories, setCategories] = useState<string[]>([]);
  const [audience, setAudience] = useState<Audience>({ traffic: 0, views: 0, dwell: 0, attention: 0, gender: { datasets: [], labels: [] }, age: { datasets: [], labels: [] }, emotion: { datasets: [], labels: [] } });
  const [previousAudience, setPreviousAudience] = useState<Audience>({ traffic: 0, views: 0, dwell: 0, attention: 0, gender: { datasets: [], labels: [] }, age: { datasets: [], labels: [] }, emotion: { datasets: [], labels: [] } });
  const [interaction, setInteraction] = useState<Interaction>({ count: 0, time: 0, most: { id: '', name: '', count: 0 }, distribution: { labels: [], datasets: [] }});
  const [previousInteraction, setPreviousInteraction] = useState<Interaction>({ count: 0, time: 0, most: { id: '', name: '', count: 0 }, distribution: { labels: [], datasets: [] }});
  const [occupancy, setOccupancy] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entries: { datasets: [], labels: [] }});
  const [previousOccupancy, setPreviousOccupancy] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entries: { datasets: [], labels: [] }});
  const [footfall, setFootfall] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entries: { datasets: [], labels: [] }});
  const [previousFootfall, setPreviousFootfall] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entries: { datasets: [], labels: [] }});
  const [crossings, setCrossings] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entries: { datasets: [], labels: [] }});
  const [previousCrossings, setPreviousCrossings] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entries: { datasets: [], labels: [] }});
  const [productSales, setProductSales] = useState<Sales>({ productSales: [], familySales: [], brandSales: [], productSalesAmount: 0, familySalesAmount: 0, brandSalesAmount: 0, productSalesUnits: 0, familySalesUnits: 0, brandSalesUnits: 0 });
  const [previousProductSales, setPreviuosProductSales] = useState<Sales>({ productSales: [], familySales: [], brandSales: [], productSalesAmount: 0, familySalesAmount: 0, brandSalesAmount:0, productSalesUnits: 0, familySalesUnits: 0, brandSalesUnits: 0 });
  const [filter, setFilter] = useState<Filter>(getFilterFromParams());
  const [displayMediaPlayback, setDisplayMediaPlayback] = useState(false);
  const [displayProductSales, setDisplayProductSales] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const filterRef = useRef(filter);
  // id of the last load started: only that one updates the cards
  const loadIdRef = useRef(0);
  const tokenTimeoutRef = useRef<NodeJS.Timeout>();
  const graphicsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const w: any = window;
    const onMessage = (event: any) => {
      // Only the embedding page (Studio) can provide tokens
      if(event.source !== w.parent || w.parent === w) {
        return;
      }
      if(event.data.token) {
        params.token = event.data.token;
        // Current filter (the listener lives for the whole session) and
        // replaceState, so a token renewal doesn't add history entries
        params.token && setParams(filterRef.current, params.token, true);
        clearTimeout(tokenTimeoutRef.current);
        // Ask for a new token 5 min before it expires, but never more often
        // than every 30 s (short-lived token or missing expiresAt)
        const expiresAt = event.data.expiresAt;
        const delay = Number.isFinite(expiresAt) ? Math.max(expiresAt - Date.now() - 300000, 30000) : 30000;
        tokenTimeoutRef.current = setTimeout(() => {
          w.parent.postMessage('new-token', '*');
        }, delay);
      }
    };
    w.addEventListener('message', onMessage, false);
    w.parent.postMessage('token-information', '*');
    load(filter);
    // The next refresh is scheduled when the previous one ends, so on a slow network
    // refreshes never pile up
    let refreshTimeout: NodeJS.Timeout | undefined;
    let unmounted = false;
    const scheduleRefresh = () => {
      refreshTimeout = setTimeout(async () => {
        await load(filterRef.current);
        !unmounted && scheduleRefresh();
      }, 60000);
    };
    autoRefresh && scheduleRefresh();
    return () => {
      w.removeEventListener('message', onMessage, false);
      clearTimeout(tokenTimeoutRef.current);
      unmounted = true;
      clearTimeout(refreshTimeout);
    };
  }, []);

  useEffect(() => {
    filterRef.current = filter;
  }, [ filter ]);

  const onChangeFilter = async (filter: Filter) => {
    console.log('onChangeFilter');
    const f = { ...filterRef.current, ...filter };
    setFilter(f);
    setParams(f, accountContext.token || '');
    await load(f);
  }

  const onDownloadPdf = async () => {
    setLoading(true);
    if(graphicsContainerRef.current) {
      const canvas = await html2canvas(graphicsContainerRef.current, { scale: 1.25 });
      const image = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      const props = pdf.getImageProperties(image);
      const width = pdf.internal.pageSize.getWidth() - 40;
      const height = (props.height * width) / props.width;
      pdf.setFillColor(248, 247, 250);
      pdf.rect(10, 10, width + 20, height + 20, 'F')
      pdf.setFont('Helvetica');
      pdf.setFontSize(8);
      pdf.setTextColor('#333333');
      // print filter information
      let info = `${t('pdf.account')}: ${params?.name}`;
      FilterService.getLocationNames(filter) && (info += ` | ${t('pdf.location')}: ${FilterService.getLocationNames(filter)}`);
      filter.installationId && (info += ` | ${t('pdf.installation')}: ${filter.installationName}`);
      filter.campaignId && (info += ` | ${t('pdf.campaign')}: ${filter.campaignName}`);
      filter.playlistId && (info += ` | Playlist: ${filter.playlistName}`);
      info += ` | ${t('pdf.period')}: ${DateTime.fromJSDate(filter.from || new Date()).setLocale(language).toLocaleString(DateTime.DATE_SHORT)} - ${DateTime.fromJSDate(filter.to || new Date()).setLocale(language).toLocaleString(DateTime.DATE_SHORT)}`;
      const splitInfo = pdf.splitTextToSize(info, width);
      let y = 20;
      for(const i of splitInfo) {
        pdf.text(i, 20, y);
        y += 6;
      }
      pdf.addImage(image, 'PNG', 20, y, width, height);
      pdf.save('analytics.pdf');
    }
    setLoading(false);
  }

  const onDownloadExcel = async () => {
    setLoading(true);
    setDownloadingExcel(true);
    try {
      const workbook = await getExcelWorkbook(params?.name || '', filter, params);
      const blob = new Blob([workbook.content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', workbook.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    finally {
      setDownloadingExcel(false);
      setLoading(false);
    }
  }

  const onRefresh = () => {
    console.log('onRefresh');
    load(filterRef.current);
  }

  const load = async (filter: Filter) => {
    console.log('load');
    // A load that finishes after a newer one has started (auto refresh or a quick filter change)
    // must not overwrite its data nor hide its loading indicator
    const id = ++loadIdRef.current;
    const isCurrent = () => id === loadIdRef.current;
    setLoading(true);
    const salesEnabled = accountContext.modules && accountContext.modules.includes('sales');
    try {
      await loadDashboard(filter, params, salesEnabled ? serviceFactory.getSaleService() : null, {
        setAudience, setPreviousAudience,
        setCategories,
        setInteraction, setPreviousInteraction,
        setOccupancy, setPreviousOccupancy,
        setFootfall, setPreviousFootfall,
        setCrossings, setPreviousCrossings,
        setDisplayMediaPlayback,
        setProductSales, setPreviousProductSales: setPreviuosProductSales, setDisplayProductSales,
        // the comparison with the previous period completes in the background
        currentLoaded: () => setLoading(false),
        isCurrent
      });
    }
    finally {
      isCurrent() && setLoading(false);
    }
  }

  const setParams = (filter: Filter, token: string, replace = false) => {
    const params = new URLSearchParams();
    filter.locationId?.forEach(locationId => params.append('location_id', locationId));
    filter.installationId && params.set('installation_id', filter.installationId);
    filter.campaignId && params.set('campaign_id', filter.campaignId);
    filter.playlistId && params.set('playlist_id', filter.playlistId);
    filter.zone && params.set('zone', filter.zone);
    filter.gate && params.set('gate', filter.gate);
    filter.crossingLine && params.set('crossing_line', filter.crossingLine);
    filter.from && params.set('start_date', DateTime.fromJSDate(filter.from).toISO() || '');
    filter.to && params.set('end_date', DateTime.fromJSDate(filter.to).toISO() || '');
    filter.compareFrom && params.set('compare_start_date', DateTime.fromJSDate(filter.compareFrom).toISO() || '');
    filter.compareTo && params.set('compare_end_date', DateTime.fromJSDate(filter.compareTo).toISO() || '');
    filter.cutoff && params.set('cutoff_time', filter.cutoff);
    filter.comparePreset && params.set('compare_preset', filter.comparePreset);
    filter.rangePreset && params.set('range_preset', filter.rangePreset);
    // Se conserva el idioma para que recargar o compartir el enlace no lo pierda
    const lang = getLanguageParam();
    lang && params.set('lang', lang);
    let url = '';
    const path = window.location.pathname.split('/');
    path.shift();
    if(path.length === 2 && token) {
      path[1] = token;
      url = `${window.location.origin}/${path.join('/')}`;
    }
    const fullUrl = `${url}?${params.toString()}`;
    replace ? history.replaceState(null, 'filter', fullUrl) : history.pushState(null, 'filter', fullUrl);
  }

  const displayInteractions = categories.includes('sensor') || categories.includes('onevent');
  const hasEmotionData = audience.emotion.datasets.some(d => (d.data as { r: number }[]).some(p => Number(p.r) > 0));
  return (
    <AccountContext.Provider value={accountContext}>
      <div className="container-xxl">
        <Header filter={filter} loading={loading} downloadingExcel={downloadingExcel} onChange={onChangeFilter} onDownloadPdf={onDownloadPdf} onDownloadExcel={onDownloadExcel} onRefresh={autoRefresh ? undefined : onRefresh} />
        <div ref={graphicsContainerRef} style={{ backgroundColor: '#f8f7fa' }}>
          {hasValues(footfall) &&
            <div className="row">
              <div className="col-6 col-lg-3 mb-4">
                <OccupancyCard total={footfall.total} title={t('cards.footfall')} informationId="footfall" color="orange" />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <EntriesCard input={footfall.entries} title={t('cards.topZones')} informationId="topZones" color="orange" />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <PeakTimeCard hour={footfall.peakTimeHour} total={footfall.peakTimeValue} previousTotal={previousFootfall.peakTimeValue} />
              </div>
              <div className="col-6 col-lg-3 mb-4">
              <OffPeakTimeCard hour={footfall.offPeakTimeHour} total={footfall.offPeakTimeValue} previousTotal={previousFootfall.offPeakTimeValue} />
              </div>
            </div>
          }
          {hasValues(crossings) &&
            <div className="row">
              <div className="col-6 col-lg-3 mb-4">
                <OccupancyCard total={crossings.total} title={t('cards.crossings')} informationId="liveOccupancy" color="purple" />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <EntriesCard input={crossings.entries} title={t('cards.topCrossingLines')} informationId="topGates" color="purple" />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <PeakTimeCard hour={crossings.peakTimeHour} total={crossings.peakTimeValue} previousTotal={previousCrossings.peakTimeValue} />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <OffPeakTimeCard hour={crossings.offPeakTimeHour} total={crossings.offPeakTimeValue} previousTotal={previousCrossings.offPeakTimeValue} />
              </div>
            </div>
          }
          {hasValues(occupancy) &&
            <div className="row">
              <div className="col-6 col-lg-3 mb-4">
                <OccupancyCard total={occupancy.total > 0 ? occupancy.total : 0} title={t('cards.liveOccupancy')} informationId="liveOccupancy" color="info" />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <EntriesCard input={occupancy.entries} title={t('cards.topGates')} informationId="topGates" color="info" />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <PeakTimeCard hour={occupancy.peakTimeHour} total={occupancy.peakTimeValue} previousTotal={previousOccupancy.peakTimeValue} />
              </div>
              <div className="col-6 col-lg-3 mb-4">
                <OffPeakTimeCard hour={occupancy.offPeakTimeHour} total={occupancy.offPeakTimeValue} previousTotal={previousOccupancy.offPeakTimeValue} />
              </div>
            </div>
          }
          <div className="row">
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title={t('cards.traffic')} value={audience.traffic} previousValue={previousAudience.traffic} color="success" icon={<TrafficIcon />} comparePreset={filter.comparePreset} informationId="traffic" />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title={t('cards.watchers')} value={audience.views} previousValue={previousAudience.views} color="primary-purple" icon={<ViewsIcon />} comparePreset={filter.comparePreset} informationId="watchers" />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title={t('cards.dwellTime')} value={audience.dwell} previousValue={previousAudience.dwell} format={(value: number) => value.toFixed(1) + 's'} color="warning" icon={<DwellIcon />} comparePreset={filter.comparePreset} informationId="dwellTime" />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title={t('cards.avgAttentionTime')} value={audience.attention} previousValue={previousAudience.attention} format={(value: number) => value.toFixed(1) + 's'} color="info" icon={<AttentionIcon />} comparePreset={filter.comparePreset} informationId="avgAttentionTime" />
            </div>
          </div>
          <div className="row d-flex flex-wrap">
            <div className={`${hasEmotionData ? 'col-4' : 'col-6'} mb-4`}>
              <GenderCard input={audience.gender} />
            </div>
            <div className={`${hasEmotionData ? 'col-4' : 'col-6'} mb-4`}>
              <AgeCard input={audience.age} />
            </div>
            {
              hasEmotionData &&
                <div className="col-4 mb-4">
                  <EmotionCard input={audience.emotion} />
                </div>
            }
          </div>
          {
            displayMediaPlayback &&
              <div className="row">
                <div className="col-12 mb-4">
                  <MediaPlaybackCard filter={filter} />
                </div>
              </div>
          }
          {
            displayProductSales &&
              <div className="row">
                <div className="col-12 mb-4">
                  <ProductSalesCard sales={productSales} previousSales={previousProductSales} comparePreset={filter.comparePreset} />
                </div>
              </div>
          }
          <div className="row">
            {
              displayInteractions ?
              <>
                <div className="col-12 col-lg-4 mb-4">
                  <div className="row h-100">
                    <div className="col-6 col-lg-12 mb-lg-4">
                      <InteractionsMetricsCard count={interaction.count} previousCount={previousInteraction.count} time={interaction.time} previousTime={previousInteraction.time} />
                    </div>
                    <div className="col-6 col-lg-12">
                      <TopInteractedCard name={interaction.most.name} count={interaction.most.count} previousCount={previousInteraction.most.count} total={interaction.count} comparePreset={filter.comparePreset} />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-4 mb-4">
                  <TopFiveCard input={interaction.distribution} previousInput={previousInteraction.distribution} />
                </div>
              </> : ''
            }
            <div className={`col-12 ${displayInteractions ? 'col-lg-4' : ''} mb-4`}>
              <HistoricalCard filter={filter} shared={displayInteractions} saleService={serviceFactory.getSaleService()} />
            </div>
          </div>
        </div>
      </div>
    </AccountContext.Provider>
  );
}

interface Props {
  params: AccountParams,
  autoRefresh: boolean,
  serviceFactory: IServiceFactory
}

export { App };
